import CiteCheckSharedEdition from '@/components/citecheck/CiteCheckSharedEdition';
import { LexChatLoadingState } from '@/features/lexRuntime/components/LexChatLoadingState';
import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useKeycloakSessionKeepAlive } from './auth/useKeycloakSessionKeepAlive';
import { clearStoredWebSession, postLoginUrl } from './auth/webSession';
import { isDesktopRuntime } from './auth/desktopAuth';
import { LoggingConfigService } from './services/LoggingConfigService';
import { AppLogger } from './utils/loggers';
import AppLayout from './layout/AppLayout';
import { useApplyAppearancePreferences, useUserPreferencesSync } from './hooks/useUserPreferences';
import { useSystemPreferences } from './hooks/useSystemPreferences';
import { useAppStore } from './store/appStore';
function SubscriptionSettingsRedirect() {
  const q = new URLSearchParams(window.location.search);
  q.set('tab', 'subscription');
  return <Navigate to={`/admin/settings?${q.toString()}`} replace />;
}
// import { WsVersion1DocumentEditorPage } from '@/features/dms/routes/wsVersion1DocumentEditorPage'; // COMMENTED OUT - Legacy unused route
import { SearchProvider } from './contexts/SearchContext';
import { setTokenGetter } from './api/axios';
import { GlobalMatterIntelligenceCapture } from '@/features/lexRuntime/components/selection/GlobalMatterIntelligenceCapture';
import { buildLegacyProjectsRedirectTarget } from '@/features/lexRuntime/legacyProjectsRedirect';
import CorpSystemToolsRoute from '@/security/CorpSystemToolsRoute';
import { accountingUiEnabled } from '@/features/accounting/accountingApplication';

const agentControlCenterEnabled =
  import.meta.env.VITE_EDGELEX_AGENT_CONTROL_CENTER === 'true';

// Test component for LexicalDocumentEditor
import { useLocation, useNavigate } from 'react-router-dom';

const SystemMonitorPage = lazy(() => import('./features/system-monitor/SystemMonitorPage'));
const WsLogViewer = lazy(() => import('./features/jobs/WsLogViewer'));
const JobTracePage = lazy(() => import('@/features/jobs/JobTracePage'));
const JobTraceGroupViewer = lazy(() => import('@/features/jobs/JobTraceGroupViewer'));
const SchemaValidationDashboard = lazy(() => import('@/features/jobs/SchemaValidationDashboard'));
const ContactsDashboard = lazy(() => import('@/features/contacts/routes/ContactsDashboard'));
const MattersDashboard = lazy(() => import('@/features/matters/routes/MattersDashboard'));
const BillingDashboard = lazy(() => import('@/features/billing/routes/BillingDashboard'));
const AccountingAppRoute = lazy(() => import('@/features/accounting/AccountingAppRoute'));
const CalendarPage = lazy(() => import('@/features/calendar/routes/CalendarPage'));
const LexicalDemo = lazy(() => import('@/features/calendar/routes/LexicalDemo').then((module) => ({ default: module.LexicalDemo })));
const UserManagement = lazy(() => import('@/features/admin/routes/UserManagement'));
const FirmSettings = lazy(() => import('@/features/admin/routes/FirmSettings'));
const EdgeMailManagementPage = lazy(() => import('@/features/edgemail-admin/routes/EdgeMailManagementPage'));
const MailClientPage = lazy(() => import('@/features/edgemail-client/routes/MailClientPage'));
const PopOutViewerPage = lazy(() => import('@/features/edgemail-client/routes/PopOutViewerPage'));
const IntakeDashboard = lazy(() => import('@/features/intake/routes/IntakeDashboard'));
const MyTasksPage = lazy(() => import('@/features/tasks/routes/MyTasksPage'));
const TaskDetailRoute = lazy(() => import('@/features/tasks/routes/TaskDetailRoute'));
const MeetHomePage = lazy(() => import('@/features/meeting/MeetHomePage'));
const MeetingDetailPage = lazy(() => import('@/features/meeting/MeetingDetailPage'));
const MeetingRoom = lazy(() => import('@/features/meeting/MeetingRoom'));
const AcceptInvitationPage = lazy(() => import('@/features/meeting/AcceptInvitationPage'));
const DMSDashboard = lazy(() => import('@/features/dms/DMSDashboard'));
const TemplatePreviewPage = lazy(() => import('@/features/dms/routes/TemplatePreviewPage').then((module) => ({ default: module.TemplatePreviewPage })));
const ClientFirstDocumentEditorPage = lazy(() => import('@/features/dms/routes/ClientFirstDocumentEditorPage').then((module) => ({ default: module.ClientFirstDocumentEditorPage })));
const CollaborationAcceptPage = lazy(() => import('@/features/dms/routes/CollaborationAcceptPage').then((module) => ({ default: module.CollaborationAcceptPage })));
const DataRoomAcceptPage = lazy(() => import('@/features/dms/dataRooms/DataRoomAcceptPage'));
const DataRoomViewerPage = lazy(() => import('@/features/dms/dataRooms/DataRoomViewerPage'));
const PdfCombinePage = lazy(() => import('./features/dms/routes/PdfCombinePage'));
const EdgeDocsEditorPage = lazy(() => import('./features/wordprocessing/pages/EdgeDocsEditorPage'));
const LexicalEditorPage = lazy(() => import('./features/wordprocessing/pages/LexicalEditorPage'));
const WordDocumentPage = lazy(() => import('./features/wordprocessing/pages/WordDocumentPage'));
const WordLauncherPage = lazy(() => import('./features/wordprocessing/pages/WordLauncherPage'));
const ParagraphInspector = lazy(() => import('@/features/dms/EdgeDocs/EdgeDocsCore/devtools/ParagraphInspector'));
const Sprint2LayoutRunner = lazy(() => import('@/features/dms/EdgeDocs/EdgeDocsCore/devtools/Sprint2LayoutRunner'));
const PageLayoutViewer = lazy(() => import('@/features/dms/EdgeDocs/EdgeDocsCore/devtools/PageLayoutViewer'));
const Sprint3PlanHashRunner = lazy(() => import('@/features/dms/EdgeDocs/EdgeDocsCore/devtools/Sprint3PlanHashRunner'));
const EdgeDocsTestLab = lazy(() => import('@/features/dms/EdgeDocs/EdgeDocsCore/devtools/EdgeDocsTestLab'));
const AstOnlyTypesetterPlayground = lazy(() => import('@/features/dms/components/edgedocs/components/EdgedocsCanvasViewer').then((module) => ({ default: module.EdgedocsCanvasViewer })));
const PaginationTestPage = lazy(() => import('./features/wordprocessing/pages/PaginationTestPage'));
const LogViewerPage = lazy(() => import('./features/logging').then((module) => ({ default: module.LogViewerPage })));
const TaskResponsePage = lazy(() => import('./pages/TaskResponsePage'));
const PublicFormPage = lazy(() => import('./features/intake/components/PublicFormPage'));
const PublicIntakeDocumentRequestPage = lazy(() => import('./features/intake/components/PublicIntakeDocumentRequestPage'));
const ROAHealthDashboard = lazy(() => import('@/features/admin/components/system-tools/roa/ROAHealthDashboard'));
const ROACourtDetailPanel = lazy(() => import('@/features/admin/components/system-tools/roa/ROACourtDetailPanel'));
const CourtOnboardingDashboard = lazy(() => import('@/features/admin/components/system-tools/court-onboarding/CourtOnboardingDashboard'));
const StateDetailPanel = lazy(() => import('@/features/admin/components/system-tools/court-onboarding/StateDetailPanel'));
const CourtSiloDetailPanel = lazy(() => import('@/features/admin/components/system-tools/court-onboarding/CourtSiloDetailPanel'));
const ChatTestRunnerPage = lazy(() => import('./pages/ChatTestRunnerPage'));
const LegacyChatLayout = lazy(() => import('@/features/chat/LegacyChatLayout'));
const LexRuntimePage = lazy(() => import('@/features/lexRuntime/components/LexRuntimePage'));
const WorkflowApprovalsPage = lazy(() => import('@/features/playbooks/components/WorkflowApprovalsPage'));
const FewShotReviewDashboard = lazy(() => import('@/features/admin/components/system-tools/few-shot-review/FewShotReviewDashboard'));
const CommunicationsDashboard = lazy(() => import('@/features/communications/routes/CommunicationsDashboard'));
const ConnectMessagesPage = lazy(() => import('@/features/connect/ConnectMessagesPage'));
const CourtCalendarPortal = lazy(() => import('@/features/admin/components/system-tools/court-calendar/CourtCalendarPortal'));
const LegalContentLibrary = lazy(() => import('@/features/admin/components/system-tools/legal-content/LegalContentLibrary'));
const LegalReasoningBenchmarkWorkbench = lazy(() => import('@/features/admin/components/system-tools/legal-reasoning-benchmarks/LegalReasoningBenchmarkWorkbench'));
const AgentsControlPlanePage = lazy(() => import('@/features/agents/routes/AgentsControlPlanePage'));
const AgentProfilesPage = lazy(() => import('@/features/agents/routes/AgentProfilesPage'));
const EdgeDocsStandalone = lazy(() => import('./features/edgedocs/EdgeDocs').then((module) => ({ default: module.EdgeDocs })));


function LexicalTestPage(): React.JSX.Element {
  const navigate = useNavigate();
  
  return (
    <div className="h-screen w-screen bg-gray-100">
      <EdgeDocsStandalone
        documentId="test-document"
        onClose={() => {
          AppLogger.component.running('EdgeDocs', { action: 'closed', navigatingTo: '/' });
          navigate('/');
        }}
      />
    </div>
  );
}

function LegacyProjectsRedirect(): React.JSX.Element {
  const { projectId } = useParams<{ projectId?: string }>();
  return (
    <Navigate
      to={buildLegacyProjectsRedirectTarget(window.location.search, projectId)}
      replace
    />
  );
}

function App() {
  AppLogger.component.running('APP COMPONENT', { message: 'BASIC REACT TEST' });
  
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const isCollaborationAcceptRoute = location.pathname === '/collaboration/accept';
  const isDataRoomAcceptRoute = location.pathname === '/data-rooms/accept';
  const publicDocumentRequestMatch = location.pathname.match(/^\/intake\/document-request\/([^/]+)$/);
  // Public intake form (/form/<uuid>): rendered for anyone, signed in or not. Embedded by firm
  // websites, where the visitor never has an EdgeLex session.
  const publicFormMatch = location.pathname.match(/^\/form\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i);
  useUserPreferencesSync();
  useApplyAppearancePreferences();
  useSystemPreferences(); // Load and apply firm-wide system preferences
  // Refresh before the realm's 30-minute session idle limit so an open app
  // never has to sign in again at token expiry. Desktop manages its own tokens.
  useKeycloakSessionKeepAlive(keycloak, initialized && !isDesktopRuntime());

  // An unauthenticated app session (outside the public accept pages) goes
  // back to EdgeLex's own login form, never to the Keycloak-hosted page.
  const sendToLoginForm = initialized && !keycloak.authenticated && !isCollaborationAcceptRoute && !isDataRoomAcceptRoute && !publicDocumentRequestMatch && !publicFormMatch;
  useEffect(() => {
    if (!sendToLoginForm || isDesktopRuntime()) return;
    clearStoredWebSession();
    window.location.replace(postLoginUrl());
  }, [sendToLoginForm]);

  const {
    setCurrentFirmId,
    setCurrentUserId,
    setIsAuthenticated,
  } = useAppStore();

  // Register the Keycloak token getter with Axios for multi-tenant authentication
  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      setTokenGetter(() => keycloak.token || null);
      AppLogger.auth.tokenRegistered(keycloak.realm || 'unknown');
    }
  }, [initialized, keycloak.authenticated, keycloak.token, keycloak.realm]);

  // Initialize LoggingConfigService early so log filtering works immediately
  useEffect(() => {
    if (initialized && keycloak.authenticated && keycloak.token) {
      // Store token for LoggingConfigService to use
      if (!window.edgelexDesktop) localStorage.setItem('edgelex_access_token', keycloak.token);
      
      // Initialize logging config with current realm
      LoggingConfigService.initialize(keycloak.realm || null)
        .then(() => {
          AppLogger.auth.loggingConfigInit(keycloak.realm || 'unknown');
        })
        .catch((err) => {
          AppLogger.auth.keycloakEvent('loggingConfigFailed', err, { realm: keycloak.realm });
        });
    }
  }, [initialized, keycloak.authenticated, keycloak.token, keycloak.realm]);

  useEffect(() => {
    if (initialized) {
      const isAuthenticated = keycloak.authenticated ?? false;
      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated && keycloak.realm && keycloak.subject) {
        setCurrentFirmId(keycloak.realm);
        setCurrentUserId(keycloak.subject);
      } else {
        setCurrentFirmId(null);
        setCurrentUserId(null);
      }
    }
  }, [
    initialized,
    keycloak.authenticated,
    keycloak.realm,
    keycloak.subject,
    setIsAuthenticated,
    setCurrentFirmId,
    setCurrentUserId,
  ]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 flex items-center justify-center transition-colors duration-300 ease-in-out">
        <p className="text-xl">Verifying firm access & initializing application...</p>
      </div>
    );
  }

  if (!keycloak.authenticated && (isCollaborationAcceptRoute || isDataRoomAcceptRoute)) {
    return <Suspense fallback={<p role="status">Loading invitation…</p>}>{isDataRoomAcceptRoute ? <DataRoomAcceptPage /> : <CollaborationAcceptPage />}</Suspense>;
  }

  if (publicDocumentRequestMatch) {
    return <Suspense fallback={<p role="status">Loading request…</p>}><PublicIntakeDocumentRequestPage token={decodeURIComponent(publicDocumentRequestMatch[1])} /></Suspense>;
  }

  if (publicFormMatch) {
    // Routes so PublicFormPage's useParams resolves; identical for signed-in and anonymous visitors.
    return (
      <Suspense fallback={<p role="status">Loading form…</p>}>
        <Routes>
          <Route path="/form/:formId" element={<PublicFormPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (!keycloak.authenticated) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 flex items-center justify-center transition-colors duration-300 ease-in-out">
        <p className="text-xl">Returning to sign in…</p>
      </div>
    );
  }

  // ✅ Authenticated layout with nested routes
  return (
    <SearchProvider>
      <Suspense fallback={<div role="status" className="flex h-full items-center justify-center text-[var(--lex-text-secondary)]">Loading workspace…</div>}>
      <Routes>
        <Route path="/" element={<AppLayout />}>

          {/* Default route → redirects to Chat */}
          <Route index element={<Navigate to="/chat" replace />} />

          <Route path="chat" element={<Suspense fallback={<LexChatLoadingState />}><LexRuntimePage variant="deerflow" /></Suspense>} />
          <Route path="chat/deerflow" element={<Navigate to="/chat" replace />} />
          <Route path="chat/classic-runtime" element={<Suspense fallback={<LexChatLoadingState />}><LexRuntimePage /></Suspense>} />
          <Route path="chat/legacy" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><p>Loading...</p></div>}><LegacyChatLayout /></Suspense>} />
          <Route path="lex-runtime" element={<Navigate to="/chat" replace />} />
          <Route path="projects" element={<LegacyProjectsRedirect />} />
          <Route path="projects/:projectId" element={<LegacyProjectsRedirect />} />
          <Route
            path="agents"
            element={agentControlCenterEnabled ? <AgentsControlPlanePage /> : <AgentProfilesPage />}
          />
          <Route path="messages" element={<ConnectMessagesPage />} />

          {/* 🔹 Client Intake Dashboard */}
          <Route path="intake/*" element={<IntakeDashboard />} />
         
          {/* 🔹 Contacts Management */}
          <Route path="contacts/*" element={<ContactsDashboard />} />

          {/* 🔹 Matters Management */}
          <Route path="matters/*" element={<MattersDashboard />} />

          {/* 🔹 Billing */}
          <Route path="billing/*" element={<BillingDashboard />} />
          {accountingUiEnabled() && <Route path="accounting/*" element={<AccountingAppRoute />} />}

          {/* 🔹 Document Editor - Client-First Implementation */}
          <Route path="documents/:documentId/editor" element={<EdgeDocsEditorPage />} />

          {/* 🔹 Document Editor - Redirect legacy WebSocket route */}
          <Route path="documents/:documentId/editor-ws" element={<Navigate to="../editor" replace />} />

          {/* 🔹 Document Editor - Legacy Routes (fallback) */}
          <Route path="documents/:documentId/editor-legacy" element={<ClientFirstDocumentEditorPage />} />
          {/* <Route path="documents/:documentId/editor-ws-legacy" element={<WsVersion1DocumentEditorPage />} /> */} {/* COMMENTED OUT - Legacy unused route */}

          {/* 🔹 Document Editor - Standalone Route (legacy) - DISABLED to prevent conflicts with ClientFirst */}
          {/* <Route path="documents/edge-docs/:documentId" element={<DMSOrchestrator />} /> */}

          {/* 🔹 Word Document Editor - MS Word Integration Phase 1 */}
          <Route path="documents/:documentId/word-editor" element={<WordDocumentPage />} />

          {/* 🔹 NEW WORD PROCESSING EDITORS - Orchestrator Architecture */}
          <Route path="/dms/combine" element={<PdfCombinePage />} />
          <Route path="/dms/editor/:docId" element={<EdgeDocsEditorPage />} />
          <Route path="/dms/editor-lexical/:docId" element={<LexicalEditorPage />} />

          {/* 🔹 Document Management */}
          {/* A bare document URL (delivery links, pasted references) opens the
              editor instead of dead-ending on the dashboard wildcard. */}
          <Route path="documents/:documentId" element={<Navigate to="editor" replace />} />
          <Route path="documents/*" element={<DMSDashboard />} />
          {/* 🔹 Template Preview (opens after sessionStorage seed) */}
          <Route path="dms/template-preview" element={<TemplatePreviewPage />} />
          
          {/* 🔹 Collaboration Invitation Acceptance */}
          <Route path="collaboration/accept" element={<CollaborationAcceptPage />} />

          {/* 🔹 EdgeMeet — Meetings & Depositions */}
          <Route path="meet/:meetingId/accept" element={<AcceptInvitationPage />} />
          <Route path="meet/:meetingId/room" element={<MeetingRoom />} />
          <Route path="meet/:meetingId" element={<MeetingDetailPage />} />
          <Route path="meet" element={<MeetHomePage />} />

          {/* 🔹 Task Management */}
          <Route path="tasks/:taskId" element={<TaskDetailRoute />} />
          <Route path="tasks" element={<MyTasksPage />} />
          <Route path="workflow-approvals" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><p>Loading approvals…</p></div>}><WorkflowApprovalsPage /></Suspense>} />

          {/* 🔹 Calendar Management */}
          <Route path="calendar" element={<CalendarPage />} />
          
          {/* 🔹 Lexical Demo - Deep Legal Entity Integration */}
          <Route path="lexical-demo" element={<LexicalDemo />} />

          {/* 🔹 Admin Tools */}
          <Route path="admin/user-management" element={<UserManagement />} />
          <Route path="admin/settings" element={<FirmSettings />} />
          {/* Subscription lives inside Firm Settings; these preserve the query
              (Stripe checkout success/cancel URLs land on /settings/billing). */}
          <Route path="admin/billing" element={<SubscriptionSettingsRedirect />} />
          <Route path="settings/billing" element={<SubscriptionSettingsRedirect />} />
          <Route path="admin/edgemail/*" element={<EdgeMailManagementPage />} />

          {/* EdgeMail Web Client (code-split) */}
          <Route path="cite-check-shares/:shareId" element={<CiteCheckSharedEdition />} />
          <Route path="email" element={
            <Suspense fallback={<div className="flex items-center justify-center h-screen bg-gray-950 text-gray-300">Loading EdgeMail...</div>}>
              <MailClientPage />
            </Suspense>
          } />

          {/* Pop-out viewer moved outside AppLayout — see below */}

          {/* 🛠️ Devtools: EdgeDocs Paragraph Inspector */}
          <Route path="dev/edgedocs/paragraph" element={<ParagraphInspector />} />
          {/* 🛠️ Devtools: EdgeDocs Sprint 2 Layout Runner */}
          <Route path="dev/edgedocs/tests/layout" element={<Sprint2LayoutRunner />} />
          {/* 🛠️ Devtools: EdgeDocs Page Layout Viewer */}
          <Route path="dev/edgedocs/pages" element={<PageLayoutViewer />} />
          {/* 🛠️ Devtools: EdgeDocs Sprint 3 PlanHash Runner */}
          <Route path="dev/edgedocs/tests/planhash" element={<Sprint3PlanHashRunner />} />
          {/* 🛠️ Devtools: EdgeDocs Test Lab */}
          <Route path="dev/edgedocs/test-lab" element={<EdgeDocsTestLab />} />
          {/* 🛠️ Devtools: EdgeDocs Pagination Test (NEW - Phase 3) */}
          <Route path="dev/edgedocs/pagination-test" element={<PaginationTestPage />} />

          {/* 🔹 AST-only Typesetter Playground (no browser layout) */}
          <Route path="edgedocs/ast-playground" element={<AstOnlyTypesetterPlayground />} />

          {/* Platform control plane: direct URLs require CORP + platform role. */}
          <Route element={<CorpSystemToolsRoute />}>
            <Route path="chat_test_runner" element={<ChatTestRunnerPage />} />
            <Route path="logs" element={<LogViewerPage />} />
            <Route path="system-monitor" element={<SystemMonitorPage />} />
            <Route path="system-monitor/ws-log" element={<WsLogViewer />} />
            <Route path="system-monitor/schema-validation" element={<SchemaValidationDashboard />} />
            <Route path="system-monitor/job-trace/:jobId" element={<JobTracePage />} />
            <Route path="system-monitor/trace-group/:traceGroupId" element={<JobTraceGroupViewer />} />
            <Route path="system-monitor/roa-health" element={<ROAHealthDashboard />} />
            <Route path="system-monitor/roa-health/:code" element={<ROACourtDetailPanel />} />
            <Route path="system-monitor/court-onboarding" element={<CourtOnboardingDashboard />} />
            <Route path="system-monitor/court-onboarding/court/:jurisdictionCode" element={<CourtSiloDetailPanel />} />
            <Route path="system-monitor/court-onboarding/:stateCode" element={<StateDetailPanel />} />
            <Route path="system-monitor/few-shot-review" element={<FewShotReviewDashboard />} />
            <Route path="system-tools/communications" element={<CommunicationsDashboard />} />
            <Route path="system-tools/court-calendars" element={<CourtCalendarPortal />} />
            <Route path="system-tools/legal-content" element={<LegalContentLibrary />} />
            <Route path="system-tools/legal-reasoning-validation" element={<LegalReasoningBenchmarkWorkbench />} />
          </Route>

          {/* 🔹 Redirects for legacy/admin paths */}
          <Route path="admin/*" element={<Navigate to="/admin/user-management" replace />} />
          <Route path="admin" element={<Navigate to="/admin/user-management" replace />} />
        </Route>

        {/* EdgeMail Pop-Out Viewer — standalone, no sidebar/header */}
        <Route path="/email/view/:mailboxId/:messageId" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#161e2e] text-gray-300">Loading message...</div>}>
            <PopOutViewerPage />
          </Suspense>
        } />

        {/* Standalone DMS-first entry used by the packaged EdgeLex for Word launcher. */}
        <Route path="/word-launcher" element={<WordLauncherPage />} />

        {/* 🔹 External Data Room entry points — standalone, member-gated APIs */}
        <Route path="/data-rooms/accept" element={<DataRoomAcceptPage />} />
        <Route path="/data-rooms/:roomId" element={<DataRoomViewerPage />} />

        {/* 🧪 TEST ROUTE - Isolated Lexical Editor */}
        <Route path="/test-lexical" element={<LexicalTestPage />} />

        {/* 🔹 Public: Task Assignment Response (no auth required) */}
        <Route path="/task-response/:token" element={<TaskResponsePage />} />

        {/* 🔹 Public: Client Intake Form (no auth required) */}
        <Route path="/form/:formId" element={<PublicFormPage />} />

        {/* 🔹 Public: one-time secure Intake document request */}
        <Route path="/intake/document-request/:token" element={<PublicIntakeDocumentRequestPage token={decodeURIComponent(location.pathname.split('/').pop() || '')} />} />

        {/* 🔹 Fallback: Unknown routes redirect to chat */}
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Routes>
      <GlobalMatterIntelligenceCapture />
    </Suspense>
    </SearchProvider>
  );
}

export default App;
